import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { cldThumb } from '../utils/img';
import { getBpBrands, getBpCohorts, getBpCohortItems, getBpTimeline } from '../api/brandPerformance';

const money = (v) => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const num = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN'));

const todayStr = () => new Date().toISOString().split('T')[0];
function addDays(iso, n) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}

const SUMMARY_CARDS = [
  ['Items in Inventory', 'items_in_inventory', num],
  ['Designs in Inventory', 'designs_in_inventory', num],
  ['Total Items Sold', 'total_items_sold', num],
  ['Total Sales', 'total_sales', money],
  ['ASP', 'asp', money],
  ['Total Revenue', 'total_revenue', money],
  ['Total Sendouts', 'total_sendouts', num],
  ['Sell Through', 'sell_through', pct],
  ['Gross Margin %', 'gross_margin_pct', pct],
];

function SummaryStrip({ s }) {
  if (!s) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-3 mb-6">
      {SUMMARY_CARDS.map(([label, key, fmt]) => (
        <div key={key} className="bg-white border border-gray-200 rounded-xl px-3 py-2.5">
          <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1 leading-tight">{label}</div>
          <div className="text-base font-black text-gray-800">{fmt(s[key])}</div>
        </div>
      ))}
    </div>
  );
}

function BrandSelect({ brands, brandId, onChange }) {
  return (
    <select value={brandId} onChange={(e) => onChange(e.target.value)}
      className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-zoddle-green">
      <option value="">Select brand…</option>
      {brands.map((b) => <option key={b.brand_partner_id} value={b.brand_partner_id}>{b.name}</option>)}
    </select>
  );
}

function CohortView({ brands }) {
  const [brandId, setBrandId] = useState('');
  const [cohorts, setCohorts] = useState([]);
  const [selCohorts, setSelCohorts] = useState(new Set());
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pieceWise, setPieceWise] = useState(false);

  useEffect(() => {
    if (!brandId) { setCohorts([]); setSelCohorts(new Set()); setItems([]); setSummary(null); return; }
    getBpCohorts(brandId).then((cs) => {
      setCohorts(cs);
      setSelCohorts(new Set(cs.slice(0, 1))); // latest first
    }).catch(() => {});
  }, [brandId]);

  const load = useCallback(async () => {
    if (!brandId || selCohorts.size === 0) { setItems([]); setSummary(null); return; }
    setLoading(true);
    try {
      const res = await getBpCohortItems(brandId, Array.from(selCohorts));
      setItems(res.items); setSummary(res.summary);
    } catch (e) { setItems([]); setSummary(null); }
    finally { setLoading(false); }
  }, [brandId, selCohorts]);
  useEffect(() => { load(); }, [load]);

  function toggleCohort(c) {
    setSelCohorts((prev) => { const n = new Set(prev); n.has(c) ? n.delete(c) : n.add(c); return n; });
  }

  const groups = useMemo(() => {
    const m = {};
    items.forEach((it) => { (m[it.inbound_date] = m[it.inbound_date] || []).push(it); });
    return Object.entries(m).sort((a, b) => String(b[0]).localeCompare(String(a[0])));
  }, [items]);

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <BrandSelect brands={brands} brandId={brandId} onChange={setBrandId} />
        {cohorts.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-400">Inbound cohorts:</span>
            {cohorts.map((c) => (
              <button key={c} onClick={() => toggleCohort(c)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition ${selCohorts.has(c) ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                {c}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center rounded-full border border-gray-200 p-0.5 text-xs font-semibold ml-auto">
          {[['false', 'Design', false], ['true', 'Piece', true]].map(([key, label, val]) => (
            <button key={key} onClick={() => setPieceWise(val)}
              className={`px-3 py-1 rounded-full transition ${pieceWise === val ? 'bg-zoddle-green text-white' : 'text-gray-500 hover:text-gray-700'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {brandId && <SummaryStrip s={summary} />}

      {loading ? (
        <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
      ) : !brandId ? (
        <div className="text-center py-16 text-gray-400 text-sm">Select a brand to begin</div>
      ) : groups.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">No items for the selected cohorts</div>
      ) : groups.map(([cohort, rows]) => (
        <div key={cohort} className="mb-8">
          <h2 className="text-sm font-bold text-gray-700 mb-2">
            Inbound cohort — {cohort} <span className="text-gray-400 font-normal">({rows.length} items)</span>
          </h2>
          <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
            <table className="w-full text-xs whitespace-nowrap">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-500">
                  <th className="px-2 py-2 text-left">Photo</th>
                  <th className="px-2 py-2 text-left">Item ID</th>
                  <th className="px-2 py-2 text-left">Status</th>
                  <th className="px-2 py-2 text-left">Inbound</th>
                  <th className="px-2 py-2 text-right">Cost +tax</th>
                  <th className="px-2 py-2 text-right">Cost</th>
                  <th className="px-2 py-2 text-right">MRP</th>
                  <th className="px-2 py-2 text-right">Zoddle</th>
                  <th className="px-2 py-2 text-right">Sold price</th>
                  <th className="px-2 py-2 text-left">Sold date</th>
                  <th className="px-2 py-2 text-left">Order ID</th>
                  <th className="px-2 py-2 text-right" title={pieceWise ? "Times this specific piece was sent to customers" : "Times this design was sent to customers"}>Sendouts</th>
                  <th className="px-2 py-2 text-right" title={pieceWise ? "Whether this specific piece has sold" : "Times this design was sold"}>Sales</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((it) => (
                  <tr key={it.item_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-2 py-1.5">
                      {it.image_url_1
                        ? <img src={cldThumb(it.image_url_1, 300)} alt="" className="w-9 h-9 rounded object-cover bg-gray-100" loading="lazy" />
                        : <div className="w-9 h-9 rounded bg-gray-100" />}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-gray-600">{it.zoddle_barcode}</td>
                    <td className="px-2 py-1.5 text-gray-600">{it.status}</td>
                    <td className="px-2 py-1.5 text-gray-600">{it.inbound_date || '—'}</td>
                    <td className="px-2 py-1.5 text-right">{money(it.cost_price_with_tax)}</td>
                    <td className="px-2 py-1.5 text-right">{money(it.cost_price)}</td>
                    <td className="px-2 py-1.5 text-right">{money(it.mrp)}</td>
                    <td className="px-2 py-1.5 text-right">{money(it.zoddle_price)}</td>
                    <td className="px-2 py-1.5 text-right">{it.sold_price != null ? money(it.sold_price) : '—'}</td>
                    <td className="px-2 py-1.5 text-gray-600">{it.sold_date || '—'}</td>
                    <td className="px-2 py-1.5 text-gray-600">{it.sold_order_id || '—'}</td>
                    <td className="px-2 py-1.5 text-right">{pieceWise ? it.item_sendouts : it.design_sendouts}</td>
                    <td className="px-2 py-1.5 text-right">{pieceWise ? it.item_sales : it.design_sales}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </>
  );
}

function TimelineView({ brands }) {
  const [brandId, setBrandId] = useState('');
  const [from, setFrom] = useState(addDays(todayStr(), -30));
  const [to, setTo] = useState(todayStr());
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!brandId) { setItems([]); setSummary(null); return; }
    setLoading(true);
    try {
      // backend window is [from, to) — send to+1 so the picked end date is inclusive
      const res = await getBpTimeline(brandId, from, addDays(to, 1));
      setItems(res.items); setSummary(res.summary);
    } catch (e) { setItems([]); setSummary(null); }
    finally { setLoading(false); }
  }, [brandId, from, to]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <BrandSelect brands={brands} brandId={brandId} onChange={setBrandId} />
        <div>
          <label className="block text-[10px] uppercase tracking-wide text-gray-400 mb-1">From</label>
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-zoddle-green" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wide text-gray-400 mb-1">To</label>
          <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-zoddle-green" />
        </div>
      </div>

      {brandId && <SummaryStrip s={summary} />}

      {loading ? (
        <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
      ) : !brandId ? (
        <div className="text-center py-16 text-gray-400 text-sm">Select a brand to begin</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">No items sold in this window</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full text-xs whitespace-nowrap">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-gray-500">
                <th className="px-2 py-2 text-left">Photo</th>
                <th className="px-2 py-2 text-left">Item ID</th>
                <th className="px-2 py-2 text-left">Design</th>
                <th className="px-2 py-2 text-right">Sold price</th>
                <th className="px-2 py-2 text-left">Sold date</th>
                <th className="px-2 py-2 text-left">Order ID</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.item_id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-2 py-1.5">
                    {it.image_url_1
                      ? <img src={cldThumb(it.image_url_1, 300)} alt="" className="w-9 h-9 rounded object-cover bg-gray-100" loading="lazy" />
                      : <div className="w-9 h-9 rounded bg-gray-100" />}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-gray-600">{it.zoddle_barcode}</td>
                  <td className="px-2 py-1.5 text-gray-600 truncate max-w-[16rem]">{it.design_name || '—'}</td>
                  <td className="px-2 py-1.5 text-right">{it.sold_price != null ? money(it.sold_price) : '—'}</td>
                  <td className="px-2 py-1.5 text-gray-600">{it.sold_date || '—'}</td>
                  <td className="px-2 py-1.5 text-gray-600">{it.sold_order_id || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default function BrandPerformance() {
  const [tab, setTab] = useState('cohort');
  const [brands, setBrands] = useState([]);

  useEffect(() => { getBpBrands().then(setBrands).catch(() => {}); }, []);

  return (
    <div className="p-6 max-w-screen-2xl mx-auto">
      <h1 className="text-2xl font-black text-gray-800 mb-1">Brand Performance</h1>
      <p className="text-sm text-gray-400 mb-5">Inventory, sales &amp; margin by brand</p>

      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {[['cohort', 'Cohort'], ['timeline', 'Timeline']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`text-sm font-semibold px-4 py-2 -mb-px border-b-2 transition ${tab === key ? 'border-zoddle-green text-zoddle-green' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'cohort' ? <CohortView brands={brands} /> : <TimelineView brands={brands} />}
    </div>
  );
}
